/**
 * SiteTrendDialog — the shared "site trend" view of the Trend feature: a station's
 * results for a parameter over a rolling 12 months, with the MCL limit line, a
 * 12-month mean line, and an optional cross-parameter "Nitrification watch" overlay
 * (rising Free Ammonia against falling chlorine — the pattern an operator needs to
 * catch, invisible in either series alone). It also carries the param/site pickers,
 * the stats grid, CSV export and the monthly-values table.
 *
 * This is the SITE MODE of the shared Trend component (features/trend). It is not
 * used directly by forms — the shared {@link TrendChartPopup} delegates to it when a
 * form passes a `siteTrend` config, so every form drives ONE Trend component
 * (standing rule 8). Originally built for the Field Data Monitoring tab (Field Data
 * Entry / Validation / Tracking, user request 2026-09-01, "Routing the Trend Icon"
 * guide Part B); generalised into the shared module + made endpoint-configurable
 * 2026-09-09 so any site-keyed form reuses it.
 *
 * House rules this touches:
 *  · Rule 8 — reuses the shared <AppDialog> shell, the shared notification
 *    service, the shared global loader (via the axios client), and the pure
 *    trendMath helpers.  It adds NO new control-chart maths.
 *  · Rule 3 — every date on the axis / tooltip goes through fmtLocal* (24-hour).
 *
 * ONE fetch feeds the whole popup: it requests EVERY field parameter at the site
 * (parameter omitted) so the parameter dropdown and the nitrification overlay
 * pivot in-memory with no refetch.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine,
} from 'recharts';
import { AppDialog } from '../../components/AppDialog';
import { api } from '../../api/client';
import { useNotify } from '../../components/notifications';
import { fmtLocalDate } from '../../utils/datetime';
import {
  axisTicks, monthKeyOf, monthSequence, monthShort, roundForChart, r1, trimZeros,
} from './trendMath';

// ── API payload (matches Application/.../FieldSiteTrendPoint) ──
interface ApiPoint {
  siteName:    string | null;
  parameter:   string | null;
  month:       string | null;   // 'yyyy-MM'
  collectDate: string | null;
  value:       number | null;
  units:       string | null;
  mcl:         number | null;
}

interface CleanPoint {
  parameter:   string;
  month:       string;
  collectDate: string | null;
  value:       number;
  units:       string;
  mcl:         number | null;
}

export interface FieldTrendJob {
  jobId:       string;
  client:      string;
  projectId:   string;
  projectName: string;
}

export interface SiteTrendProps {
  open: boolean;
  onClose: () => void;
  /** Station (SampleSites.SiteName) resolved from the monitoring row.  Empty when
   *  the popup was opened from the toolbar "Trending" button (manual pick). */
  siteName: string;
  /** Parameter the row is for — the popup opens focused on it. */
  parameter: string;
  /** All station names for the job, so the toolbar-opened popup can pick one. */
  siteOptions?: string[];
  /** Header context. */
  job: FieldTrendJob | null;
  /** The site-trend data endpoint (GET, `?siteName=&months=`, returning ApiPoint[]).
   *  Defaults to Field Data's endpoint; a different site-keyed form overrides it so
   *  the ONE shared view serves any source. */
  endpoint?: string;
}

const DEFAULT_ENDPOINT = '/api/v1/field-data-entry/site-trend';

const WINDOW_MONTHS = 12;
const MAIN_COLOR = '#e8670a';
const MCL_COLOR  = '#d92d20';
const AVG_COLOR  = '#475467';   // neutral slate — the 12-month mean reference line
const AMMONIA_COLOR = '#2563eb';
const CHLORINE_COLOR = '#0e8a8a';

// Parameters the nitrification watch overlays (case-insensitive contains).
const AMMONIA_MATCH  = 'ammonia';
const CHLORINE_MATCH = ['total residual chlorine', 'free residual chlorine', 'monochloramine'];

const isAmmonia  = (p: string) => p.toLowerCase().includes(AMMONIA_MATCH);
const isChlorine = (p: string) => CHLORINE_MATCH.some(c => p.toLowerCase().includes(c));

export function SiteTrendDialog({ open, onClose, siteName, parameter, siteOptions = [], job, endpoint = DEFAULT_ENDPOINT }: SiteTrendProps) {
  const notify = useNotify();
  const [points, setPoints] = useState<CleanPoint[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [site, setSite] = useState(siteName);
  const [param, setParam] = useState(parameter);
  const [showLimits, setShowLimits] = useState(true);
  const [watch, setWatch] = useState(false);
  const [showTable, setShowTable] = useState(false);

  // Re-seed the pickers each time the popup opens for a new row.
  useEffect(() => {
    if (!open) return;
    setSite(siteName);
    setParam(parameter);
    setWatch(false);
    setShowTable(false);
  }, [open, siteName, parameter]);

  // ONE fetch per (open, site): every field parameter at the station in the window.
  useEffect(() => {
    if (!open || !site) { setPoints(site ? null : []); return; }
    let alive = true;
    setLoading(true);
    api
      .get(endpoint, { params: { siteName: site, months: WINDOW_MONTHS } })
      .then((res) => {
        if (!alive) return;
        const raw: ApiPoint[] = res.data?.data ?? [];
        const clean: CleanPoint[] = raw
          .filter((p) => p.parameter && p.month && p.value !== null)
          .map((p) => ({
            parameter: p.parameter as string,
            month: p.month as string,
            collectDate: p.collectDate ?? null,
            value: Number(p.value),
            units: p.units ?? '',
            mcl: p.mcl === null || p.mcl === undefined ? null : Number(p.mcl),
          }));
        setPoints(clean);
      })
      .catch((e) => {
        if (!alive) return;
        notify.error(e?.response?.data?.error?.message ?? e?.message ?? 'Failed to load trend');
        setPoints([]);
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [open, site, notify, endpoint]);

  // Parameters actually present at this site (drives the dropdown).
  const paramOptions = useMemo(
    () => Array.from(new Set((points ?? []).map((p) => p.parameter))).sort((a, b) => a.localeCompare(b)),
    [points],
  );

  // Keep the selected parameter valid once data arrives.
  useEffect(() => {
    if (!points || points.length === 0) return;
    if (!param || !paramOptions.includes(param)) setParam(paramOptions[0] ?? '');
  }, [points, paramOptions, param]);

  // month → latest reading (by collect date) for one parameter.
  const monthlyOf = useMemo(() => (pname: string) => {
    const cell = new Map<string, { v: number; cd: string; units: string }>();
    (points ?? []).filter((p) => p.parameter === pname).forEach((p) => {
      const cd = p.collectDate ?? p.month;
      const cur = cell.get(p.month);
      if (cur === undefined || cd >= cur.cd) cell.set(p.month, { v: p.value, cd, units: p.units });
    });
    return cell;
  }, [points]);

  // The rolling window ends at the current month (matches the SP's GETDATE anchor).
  const months = useMemo(() => {
    const present = Array.from(new Set((points ?? []).map((p) => p.month))).sort();
    const nowM = monthKeyOf(new Date());
    const newest = present.length ? (present[present.length - 1] as string) : '';
    const anchor = newest && newest > nowM ? newest : nowM;
    return present.length ? monthSequence(anchor, WINDOW_MONTHS) : [];
  }, [points]);

  const mainUnits = useMemo(
    () => (points ?? []).find((p) => p.parameter === param && p.units)?.units ?? '',
    [points, param],
  );
  const mainMcl = useMemo(
    () => (points ?? []).find((p) => p.parameter === param && p.mcl !== null)?.mcl ?? null,
    [points, param],
  );

  // Overlay partners for the nitrification watch: one ammonia + one chlorine
  // series (whichever the site actually has), excluding the main series.
  const overlayParams = useMemo(() => {
    if (!watch) return [] as { name: string; color: string }[];
    const out: { name: string; color: string }[] = [];
    const amm = paramOptions.find((p) => isAmmonia(p) && p !== param);
    const chl = paramOptions.find((p) => isChlorine(p) && p !== param);
    if (amm) out.push({ name: amm, color: AMMONIA_COLOR });
    if (chl) out.push({ name: chl, color: CHLORINE_COLOR });
    return out;
  }, [watch, paramOptions, param]);

  const series = useMemo(
    () => [{ name: param, color: MAIN_COLOR, main: true }, ...overlayParams.map((o) => ({ ...o, main: false }))],
    [param, overlayParams],
  );

  const chartData = useMemo(() => {
    const cells = new Map(series.map((s) => [s.name, monthlyOf(s.name)]));
    return months.map((m) => {
      const row: Record<string, string | number | null> = { month: m, label: monthShort(m) };
      series.forEach((s) => {
        const c = cells.get(s.name)?.get(m);
        row[s.name] = c ? roundForChart(c.v) : null;
      });
      return row;
    });
  }, [months, series, monthlyOf]);

  // Stats for the main parameter.
  const stat = useMemo(() => {
    const vals = Array.from(monthlyOf(param).values()).map((c) => c.v);
    if (vals.length === 0) return null;
    const min = Math.min(...vals), max = Math.max(...vals);
    const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
    const latest = Array.from(monthlyOf(param).entries()).sort((a, b) => a[0].localeCompare(b[0])).at(-1)?.[1].v ?? null;
    return { latest, avg, min, max, n: vals.length };
  }, [monthlyOf, param]);

  // Y axis: a ceiling that clears both the data and the MCL line.
  const yAxis = useMemo(() => {
    const vals = chartData.flatMap((row) =>
      series.map((s) => row[s.name]).filter((v): v is number => typeof v === 'number'));
    const dataMax = vals.length ? Math.max(...vals) : 0;
    const ceil = showLimits && mainMcl !== null ? Math.max(dataMax, mainMcl) : dataMax;
    return axisTicks(ceil * 1.12 || 1);
  }, [chartData, series, showLimits, mainMcl]);

  // Nitrification signal: ammonia rising while chlorine falls over the window
  // (first-half avg vs second-half avg).  Drives the alert banner.
  const nitrification = useMemo(() => {
    const half = (name: string, second: boolean) => {
      const c = monthlyOf(name);
      const ms = months.filter((m) => c.has(m));
      if (ms.length < 4) return null;
      const seg = second ? ms.slice(Math.ceil(ms.length / 2)) : ms.slice(0, Math.floor(ms.length / 2));
      const vs = seg.map((m) => c.get(m)!.v);
      return vs.length ? vs.reduce((a, b) => a + b, 0) / vs.length : null;
    };
    const amm = paramOptions.find(isAmmonia);
    const chl = paramOptions.find(isChlorine);
    if (!amm || !chl) return false;
    const a0 = half(amm, false), a1 = half(amm, true), c0 = half(chl, false), c1 = half(chl, true);
    if (a0 === null || a1 === null || c0 === null || c1 === null) return false;
    return a1 > a0 * 1.15 && c1 < c0 * 0.9;
  }, [monthlyOf, months, paramOptions]);

  const noData = !loading && (chartData.length === 0 || series.every((s) => chartData.every((r) => r[s.name] == null)));

  // Export the trend — the visible month-by-month series (the plotted parameter
  // + any overlay series) as a CSV, matching the reference "Export chart" button.
  const exportChart = () => {
    if (chartData.length === 0) { notify.info('Nothing to export yet.'); return; }
    const cols = series.map((s) => s.name);
    const head = ['Month', ...cols.map((c) => `${c}${mainUnits ? ` (${mainUnits})` : ''}`)];
    const lines = chartData.map((row) =>
      [monthShort(row.month as string), ...cols.map((c) => (typeof row[c] === 'number' ? r1(row[c] as number) : ''))]
        .map((v) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v)).join(','));
    const csv = [head.join(','), ...lines].join('\r\n');
    const safe = (s: string) => s.replace(/[^\w.-]+/g, '_');
    const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `trend_${safe(site)}_${safe(param)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    notify.success('Exported to CSV successfully.');
  };

  const header = (
    <div>
      <div style={{ fontSize: 16, fontWeight: 700 }}>{WINDOW_MONTHS}-Month Trend</div>
      {job && (
        <div style={{ fontSize: 12, color: 'var(--ai-muted,#6b7280)', marginTop: 2 }}>
          JobID {job.jobId} · Client {job.client}{job.projectId ? ` · Project ${job.projectId}` : ''}
        </div>
      )}
    </div>
  );

  return (
    <AppDialog open={open} onClose={onClose} title={header} icon="ti-trending-up" width={860} maximizable>
      {/* selectors */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 12 }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: 'var(--ai-muted,#6b7280)', fontWeight: 600 }}>
          Site name
          {siteOptions.length > 0 ? (
            <select value={site} onChange={(e) => setSite(e.target.value)}
              style={{ height: 32, minWidth: 200, borderRadius: 6, border: '1px solid #d7dbe3', padding: '0 8px', fontSize: 13 }}>
              <option value="">— select site —</option>
              {siteOptions.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          ) : (
            <span style={{ height: 32, minWidth: 200, display: 'flex', alignItems: 'center', padding: '0 8px',
              border: '1px solid #e3e6ec', borderRadius: 6, background: '#f7f8fa', color: '#3a414e', fontSize: 13 }}>
              {site || '—'}
            </span>
          )}
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: 'var(--ai-muted,#6b7280)', fontWeight: 600 }}>
          Parameter
          <select value={param} onChange={(e) => setParam(e.target.value)}
            style={{ height: 32, minWidth: 200, borderRadius: 6, border: '1px solid #d7dbe3', padding: '0 8px', fontSize: 13 }}>
            {paramOptions.length === 0 && <option value="">—</option>}
            {paramOptions.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: 'var(--ai-muted,#6b7280)', fontWeight: 600 }}>
          Range
          <span style={{ height: 32, minWidth: 140, display: 'flex', alignItems: 'center', padding: '0 8px',
            border: '1px solid #e3e6ec', borderRadius: 6, background: '#f7f8fa', color: '#6b7280', fontSize: 13 }}>
            Last {WINDOW_MONTHS} months
          </span>
        </label>
      </div>

      {/* nitrification alert */}
      {nitrification && (
        <div style={{ display: 'flex', gap: 8, padding: '9px 12px', borderRadius: 6, marginBottom: 12,
          background: '#fef2f2', border: '1px solid #fbc5c5', color: '#b42318', fontSize: 12.5 }}>
          <b>Possible nitrification.</b>
          <span style={{ color: '#7f1d1d' }}>
            Free ammonia is climbing while chlorine falls over the window. Turn on Nitrification watch to compare.
          </span>
        </div>
      )}

      {/* toggles */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
        <Toggle on={showLimits} onClick={() => setShowLimits((v) => !v)} label="Control limits"
          disabled={mainMcl === null} title={mainMcl === null ? 'No MCL configured for this parameter' : undefined} />
        <Toggle on={watch} onClick={() => setWatch((v) => !v)} label="Nitrification watch" accent
          disabled={overlayParams.length === 0 && !watch && !(paramOptions.some(isAmmonia) && paramOptions.some(isChlorine))}
          title="Overlay Free Ammonia and chlorine on the same axis" />
        <span style={{ flex: 1 }} />
        <button className="ca-btn" onClick={exportChart} disabled={loading || noData} title="Export the monthly trend values to CSV">
          Export chart
        </button>
      </div>

      {/* chart */}
      {loading ? (
        <div style={{ height: 320, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280' }}>Loading trend…</div>
      ) : noData ? (
        <div style={{ height: 200, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, color: '#6b7280' }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#1f2430' }}>No field results to trend</div>
          <div style={{ fontSize: 12.5 }}>
            {site ? `No numeric ${param || 'field'} results were found at “${site}” in the last ${WINDOW_MONTHS} months.` : 'Pick a site and parameter to see its trend.'}
          </div>
        </div>
      ) : (
        <>
          <div style={{ height: 320, border: '1px solid #e5e7eb', borderRadius: 8, padding: '12px 8px 4px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 12, right: 64, bottom: 24, left: 8 }}>
                <CartesianGrid stroke="#eef1f6" vertical={false} />
                <XAxis dataKey="label" fontSize={11.5} tick={{ fill: '#6b7280' }} />
                <YAxis domain={[0, yAxis.max]} ticks={yAxis.ticks} fontSize={11.5} tick={{ fill: '#6b7280' }}
                  label={{ value: mainUnits || 'value', angle: -90, position: 'insideLeft', fontSize: 11.5, fill: '#6b7280' }} />
                <Tooltip
                  formatter={(v: number, name: string) => [`${r1(v)}${mainUnits ? ` ${mainUnits}` : ''}`, name]}
                  labelFormatter={(l: string, payload) => {
                    const cd = (payload?.[0]?.payload as { month?: string })?.month;
                    const meta = cd ? monthlyOf(param).get(cd) : undefined;
                    return meta && meta.cd.length >= 10 ? fmtLocalDate(meta.cd) : l;
                  }}
                  contentStyle={{ fontSize: 12 }} />
                {showLimits && mainMcl !== null && (
                  <ReferenceLine y={mainMcl} stroke={MCL_COLOR} strokeDasharray="6 4"
                    label={{ value: `MCL ${trimZeros(mainMcl)} ${mainUnits}`.trim(), position: 'insideTopRight', fontSize: 11, fill: MCL_COLOR }} />
                )}
                {/* 12-month AVERAGE (mean) reference line — user request 2026-09-05. */}
                {stat && (
                  <ReferenceLine y={stat.avg} stroke={AVG_COLOR} strokeDasharray="5 4"
                    label={{ value: `Avg ${r1(stat.avg)}${mainUnits ? ` ${mainUnits}` : ''}`.trim(), position: 'insideTopLeft', fontSize: 11, fill: AVG_COLOR }} />
                )}
                {series.map((s) => (
                  <Line key={s.name} type="linear" dataKey={s.name} name={s.name} stroke={s.color}
                    strokeWidth={s.main ? 2.6 : 1.8} strokeDasharray={s.main ? undefined : '4 3'}
                    dot={{ r: s.main ? 3.2 : 0, fill: s.color, strokeWidth: 0 }} activeDot={{ r: 5 }}
                    connectNulls isAnimationActive={false} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* legend (overlay) */}
          {series.length > 1 && (
            <div style={{ display: 'flex', gap: 16, marginTop: 8, flexWrap: 'wrap', fontSize: 12 }}>
              {series.map((s) => (
                <span key={s.name} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#374151' }}>
                  <span style={{ width: 16, height: 0, borderTop: `3px ${s.main ? 'solid' : 'dashed'} ${s.color}` }} />
                  {s.name}
                </span>
              ))}
            </div>
          )}

          {/* stats */}
          {stat && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10, marginTop: 14 }}>
              <Stat label="Latest" value={stat.latest !== null ? r1(stat.latest) : '—'} unit={mainUnits} hi />
              <Stat label={`${WINDOW_MONTHS}-mo avg`} value={r1(stat.avg)} unit={mainUnits} />
              <Stat label="Min" value={r1(stat.min)} unit={mainUnits} />
              <Stat label="Max" value={r1(stat.max)} unit={mainUnits} />
              <Stat label="Points" value={String(stat.n)} unit="" />
            </div>
          )}

          {/* monthly values table */}
          <button className="ca-btn" style={{ marginTop: 14 }} onClick={() => setShowTable((v) => !v)}>
            {showTable ? 'Hide' : 'Show'} monthly values
          </button>
          {showTable && (
            <div style={{ marginTop: 10, border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                <thead>
                  <tr style={{ background: '#fafafa', color: '#6b7280', textAlign: 'left' }}>
                    <th style={{ padding: '8px 12px' }}>Month</th>
                    <th style={{ padding: '8px 12px', textAlign: 'right' }}>{param} ({mainUnits || 'value'})</th>
                  </tr>
                </thead>
                <tbody>
                  {months.map((m, i) => {
                    const c = monthlyOf(param).get(m);
                    return (
                      <tr key={m} style={{ background: i % 2 ? '#fbf9f7' : '#fff' }}>
                        <td style={{ padding: '7px 12px' }}>{monthShort(m)}</td>
                        <td style={{ padding: '7px 12px', textAlign: 'right' }}>{c ? r1(c.v) : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </AppDialog>
  );
}

/** The 30×30 trend icon button rendered in the Monitoring grid's "Trend" cell
 *  (and the toolbar "Trending" button uses the same glyph).  Disabled with the
 *  reason as its tooltip when the row has no resolvable station. */
export function TrendIconButton({ onClick, disabledReason, title }: {
  onClick: () => void; disabledReason?: string; title?: string;
}) {
  const disabled = !!disabledReason;
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      title={disabledReason || title || 'Concentration trend'}
      aria-label="Trend"
      style={{
        width: 30, height: 30, borderRadius: 7, display: 'inline-flex', alignItems: 'center',
        justifyContent: 'center', border: '1px solid #e5e7eb', background: '#fff',
        cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.4 : 1, padding: 0,
      }}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#e8670a" strokeWidth="2.4"
        strokeLinecap="round" strokeLinejoin="round"><path d="M3 17l5-5 4 3 7-8" /><path d="M15 4h5v5" /></svg>
    </button>
  );
}

function Toggle({ on, onClick, label, accent, disabled, title }: {
  on: boolean; onClick: () => void; label: string; accent?: boolean; disabled?: boolean; title?: string;
}) {
  const color = accent ? '#dc2626' : '#e8670a';
  return (
    <button onClick={onClick} disabled={disabled} title={title}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 8, height: 32, padding: '0 12px', borderRadius: 8,
        cursor: disabled ? 'not-allowed' : 'pointer', fontSize: 12.5, fontWeight: 600, fontFamily: 'inherit',
        opacity: disabled ? 0.5 : 1,
        border: `1px solid ${on ? color : '#d7dbe3'}`,
        background: on ? (accent ? '#fef2f2' : '#fdf0e8') : '#fff',
        color: on ? (accent ? '#b42318' : '#c9500f') : '#374151',
      }}>
      <span style={{ width: 28, height: 15, borderRadius: 999, position: 'relative', background: on ? color : '#d1d5db', transition: '.15s' }}>
        <b style={{ position: 'absolute', top: 2, left: on ? 15 : 2, width: 11, height: 11, borderRadius: 999, background: '#fff', transition: '.15s' }} />
      </span>
      {label}
    </button>
  );
}

function Stat({ label, value, unit, hi }: { label: string; value: string; unit: string; hi?: boolean }) {
  return (
    <div style={{ border: `1px solid ${hi ? '#f4c9af' : '#e5e7eb'}`, borderRadius: 8, padding: '9px 12px', background: hi ? '#fdf0e8' : '#fff' }}>
      <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 17, fontWeight: 700, marginTop: 2, color: hi ? '#c9500f' : '#1f2430', fontVariantNumeric: 'tabular-nums' }}>
        {value}{unit ? <span style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginLeft: 3 }}>{unit}</span> : null}
      </div>
    </div>
  );
}
